import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Repeat, TrendingUp, Calendar as CalIcon, Wallet } from "lucide-react";

const fmt = (n) => Number(n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + " €";
const MONTH_FR = ["Jan", "Fév", "Mars", "Avr", "Mai", "Juin", "Juil", "Août", "Sep", "Oct", "Nov", "Déc"];

export default function AdminSubscriptions() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/subscriptions").then((r) => setData(r.data)).catch(() => toast.error("Erreur"));
  }, []);

  if (!data) return <div data-testid="admin-subscriptions" className="text-sm text-[#64748B] py-10 text-center">Chargement…</div>;

  const maxAmount = Math.max(1, ...data.projection_12m.map((p) => p.amount_ttc));

  return (
    <div data-testid="admin-subscriptions">
      <div>
        <h1 className="display text-2xl sm:text-3xl text-[#0A1128] font-extrabold">Abonnements & Trésorerie</h1>
        <p className="text-sm text-[#64748B] mt-1">MRR par mois, ARR, projection 12 mois, échéanciers actifs.</p>
      </div>

      {/* KPIs */}
      <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile color="#06B6D4" icon={Repeat} label="MRR" value={fmt(data.mrr_ttc)} sub="TTC mensuel récurrent" />
        <Tile color="#A7F3D0" icon={TrendingUp} label="ARR" value={fmt(data.arr_ttc)} sub="TTC annuel récurrent" />
        <Tile color="#FFDCA8" icon={Wallet} label="Abonnements actifs" value={data.subscriptions.length} sub="contrats récurrents" />
        <Tile color="#67E8F9" icon={CalIcon} label="Échéances actives" value={data.installments.length} sub="paiements échelonnés" />
      </div>

      {/* Projection 12 mois */}
      <div className="mt-4 bg-white border border-black/8 rounded-2xl p-5">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div className="display font-extrabold text-[#0A1128]">Projection de trésorerie — 12 prochains mois (TTC)</div>
          <div className="flex items-center gap-3 text-[10px] font-bold uppercase tracking-wider">
            <span className="inline-flex items-center gap-1.5 text-[#64748B]">
              <span className="w-3 h-3 rounded-sm" style={{ background: "linear-gradient(180deg, #06B6D4 0%, #67E8F9 100%)" }} /> Échéances
            </span>
            <span className="inline-flex items-center gap-1.5 text-[#64748B]">
              <span className="w-3 h-3 rounded-sm" style={{ background: "linear-gradient(180deg, #A7F3D0 0%, #6EE7B7 100%)" }} /> Mensuels
            </span>
            <span className="inline-flex items-center gap-1.5 text-[#64748B]">
              <span className="w-3 h-3 rounded-sm" style={{ background: "linear-gradient(180deg, #FFDCA8 0%, #FCD34D 100%)" }} /> Annuels
            </span>
          </div>
        </div>
        <div className="grid grid-cols-12 gap-1.5" data-testid="projection-12m">
          {data.projection_12m.map((p) => {
            const ins = parseFloat(p.installments_ttc || 0);
            const rm = parseFloat(p.recurring_monthly_ttc || 0);
            const ry = parseFloat(p.recurring_yearly_ttc || 0);
            const total = p.amount_ttc;
            const maxH = 180;
            const hIns = total > 0 ? Math.round((ins / maxAmount) * maxH) : 0;
            const hRm = total > 0 ? Math.round((rm / maxAmount) * maxH) : 0;
            const hRy = total > 0 ? Math.round((ry / maxAmount) * maxH) : 0;
            const totalH = Math.max(6, hIns + hRm + hRy);
            const [yy, mm] = p.month.split("-");
            const tooltip = [
              ins > 0 ? `Échéances : ${fmt(ins)}` : null,
              rm > 0 ? `Récurrent mensuel : ${fmt(rm)}` : null,
              ry > 0 ? `Récurrent annuel : ${fmt(ry)}` : null,
              `── ${fmt(total)} ──`,
            ].filter(Boolean).join("\n");
            return (
              <div
                key={p.month}
                className="flex flex-col items-center justify-end group"
                title={`${p.month}\n${tooltip}`}
                data-testid={`projection-month-${p.month}`}
              >
                <div className="text-[10px] font-bold tabular-nums text-[#0A1128] mb-0.5">{fmt(total)}</div>
                <div className="w-full flex flex-col-reverse rounded-t-lg overflow-hidden" style={{ height: `${totalH}px` }}>
                  {hIns > 0 && (
                    <div
                      className="w-full"
                      style={{ height: `${hIns}px`, background: "linear-gradient(180deg, #06B6D4 0%, #67E8F9 100%)" }}
                      data-testid={`bar-installments-${p.month}`}
                    />
                  )}
                  {hRm > 0 && (
                    <div
                      className="w-full"
                      style={{ height: `${hRm}px`, background: "linear-gradient(180deg, #A7F3D0 0%, #6EE7B7 100%)" }}
                      data-testid={`bar-recurring-month-${p.month}`}
                    />
                  )}
                  {hRy > 0 && (
                    <div
                      className="w-full"
                      style={{ height: `${hRy}px`, background: "linear-gradient(180deg, #FFDCA8 0%, #FCD34D 100%)" }}
                      data-testid={`bar-recurring-year-${p.month}`}
                    />
                  )}
                  {totalH === 6 && (
                    <div className="w-full h-full bg-black/5" />
                  )}
                </div>
                <div className="text-[10px] text-[#64748B] mt-1.5 font-bold uppercase">{MONTH_FR[parseInt(mm) - 1]}</div>
                <div className="text-[9px] text-[#94a3b8]">{yy}</div>
              </div>
            );
          })}
        </div>
        <div className="text-[11px] text-[#64748B] mt-3 leading-relaxed">
          Barres empilées : <span className="text-[#06B6D4] font-bold">échéances dues ce mois</span> +{" "}
          <span className="text-[#10B981] font-bold">abonnements mensuels actifs</span> (à partir de la date de livraison du projet) +{" "}
          <span className="text-[#D97706] font-bold">abonnements annuels</span> (sur leur mois anniversaire).
          {data.pending_delivery_count > 0 && (
            <span className="block mt-1 text-[#92400E] font-bold">
              ⚠️ {data.pending_delivery_count} service(s) récurrent(s) en attente — définissez la date de livraison du projet pour les inclure.
            </span>
          )}
        </div>
      </div>

      {/* Abonnements list */}
      <div className="mt-4 bg-white border border-black/8 rounded-2xl overflow-hidden">
        <div className="px-4 py-3 border-b border-black/8 bg-[#FAFAFA] display font-extrabold text-[#0A1128]">Abonnements actifs</div>
        {data.subscriptions.length === 0 ? (
          <div className="p-8 text-center text-sm text-[#64748B]">Aucun abonnement récurrent actif pour le moment.</div>
        ) : (
          <table className="w-full text-sm" data-testid="subs-table">
            <thead className="bg-[#FAFAFA] border-b border-black/8 text-[10px] font-bold tracking-widest uppercase text-[#64748B]">
              <tr>
                <th className="text-left px-3 py-2.5">Client / Devis</th>
                <th className="text-left px-3 py-2.5">Prestation</th>
                <th className="text-center px-3 py-2.5">Cycle</th>
                <th className="text-right px-3 py-2.5">HT / cycle</th>
                <th className="text-right px-3 py-2.5">TTC / cycle</th>
                <th className="text-right px-3 py-2.5">TTC / mois</th>
                <th className="text-left px-3 py-2.5">Démarré</th>
              </tr>
            </thead>
            <tbody>
              {data.subscriptions.map((s) => (
                <tr key={s.id} className="border-b border-black/5 last:border-0 hover:bg-[#FAFAFA]" data-testid={`sub-${s.id}`}>
                  <td className="px-3 py-3">
                    <div className="display font-bold text-[#0A1128]">{s.client_name || "—"}</div>
                    <div className="text-[10px] text-[#06B6D4]">{s.proposal_ref}</div>
                    {s.company_name && <div className="text-[10px] text-[#64748B]">{s.company_name}</div>}
                  </td>
                  <td className="px-3 py-3 text-xs text-[#0A1128] max-w-xs">{s.label}</td>
                  <td className="px-3 py-3 text-center">
                    <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${s.billing_cycle === "month" ? "bg-[#06B6D4]/15 text-[#06B6D4]" : "bg-[#FFDCA8] text-[#92400E]"}`}>
                      {s.billing_cycle === "month" ? "Mensuel" : "Annuel"}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-right tabular-nums text-[#64748B]">{fmt(s.amount_ht_per_cycle)}</td>
                  <td className="px-3 py-3 text-right tabular-nums font-bold text-[#0A1128]">{fmt(s.amount_ttc_per_cycle)}</td>
                  <td className="px-3 py-3 text-right tabular-nums font-extrabold text-[#06B6D4]">{fmt(s.billing_cycle === "month" ? s.amount_ttc_per_cycle : s.amount_ttc_per_cycle / 12)}</td>
                  <td className="px-3 py-3 text-xs text-[#475569]">{(s.started_at || "").slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Installments */}
      <div className="mt-4 bg-white border border-black/8 rounded-2xl overflow-hidden">
        <div className="px-4 py-3 border-b border-black/8 bg-[#FAFAFA] display font-extrabold text-[#0A1128]">Échéanciers actifs</div>
        {data.installments.length === 0 ? (
          <div className="p-8 text-center text-sm text-[#64748B]">Aucun échéancier actif (paiement en plusieurs fois).</div>
        ) : (
          <table className="w-full text-sm" data-testid="installments-table">
            <thead className="bg-[#FAFAFA] border-b border-black/8 text-[10px] font-bold tracking-widest uppercase text-[#64748B]">
              <tr>
                <th className="text-left px-3 py-2.5">Devis</th>
                <th className="text-left px-3 py-2.5">Client</th>
                <th className="text-left px-3 py-2.5">Échéance</th>
                <th className="text-left px-3 py-2.5">Date</th>
                <th className="text-right px-3 py-2.5">Montant</th>
                <th className="text-right px-3 py-2.5">Restant</th>
              </tr>
            </thead>
            <tbody>
              {data.installments.map((i) => (
                <tr key={i.id} className="border-b border-black/5 last:border-0">
                  <td className="px-3 py-3 text-xs text-[#06B6D4] font-bold">{i.proposal_ref}</td>
                  <td className="px-3 py-3 text-xs text-[#0A1128]">{i.client_name}</td>
                  <td className="px-3 py-3 text-xs text-[#475569]">{i.label}</td>
                  <td className="px-3 py-3 text-xs text-[#475569]">{(i.due_at || "").slice(0, 10)}</td>
                  <td className="px-3 py-3 text-right tabular-nums font-bold text-[#0A1128]">{fmt(i.amount)}</td>
                  <td className="px-3 py-3 text-right tabular-nums text-[#64748B]">{fmt(i.remaining)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Tile({ color, icon: Icon, label, value, sub }) {
  return (
    <div className="bg-white border border-black/8 rounded-2xl p-4 relative overflow-hidden">
      <div className="absolute top-0 left-0 h-full w-1" style={{ background: color }} />
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest font-bold text-[#64748B] pl-1">{label}</div>
        <Icon size={14} style={{ color }} />
      </div>
      <div className="display text-2xl font-extrabold text-[#0A1128] mt-1 tabular-nums pl-1">{value}</div>
      <div className="text-[11px] text-[#64748B] pl-1">{sub}</div>
    </div>
  );
}
